import React, { useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Tabs, Tab, useTheme, useMediaQuery, FormControl, Select, MenuItem, Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';

import { translate as t } from 'hooks/useTranslations';
import { useDebounce } from 'hooks/useDebounce';
import errorHelper from 'helpers/errorHelper';
import { QuickSearch } from 'components/_atoms';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { PhxBadgeCounter } from '_lex/_atoms/PhxBadgeCounter/PhxBadgeCounter';
import { AppContext } from 'views/App/context';
import { RiskPoolSharingContext } from '../context';
import { createRiskPoolRelation } from '../_data_sources/useManageRiskPoolOrganizations';
import { useSelectedOrganizations } from './_hooks/useSelectedOrganizations';
import AvailableOrganizationsTab from './tabs/AvailableOrganizationsTab';
import SelectedOrganizationsTab from './tabs/SelectedOrganizationsTab';

const SearchOrganizations: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentOrganizationId, currentProfile } = useContext(AppContext);
  const { organizations: existingOrganizations, queryClient } = useContext(RiskPoolSharingContext);

  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Shared filter state
  const [searchInput, setSearchInput] = useState('');
  const [jurisdictionInput, setJurisdictionInput] = useState<string>('');

  // Available filter options (populated by AvailableOrganizationsTab)
  const [availableJurisdictions, setAvailableJurisdictions] = useState<Array<{ value: string; label: string; }>>([]);

  // Debounced filter values
  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedJurisdiction = useDebounce(jurisdictionInput, 300);

  const {
    selectedOrganizations,
    toggleOrganization,
    isSelected,
    selectedCount,
  } = useSelectedOrganizations();

  // Get IDs of organizations already in the risk pool
  const existingOrgIds = useMemo(() => {
    return existingOrganizations.map((org) => org.id);
  }, [existingOrganizations]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleContinue = () => {
    if (selectedCount === 0) {
      enqueueSnackbar(t('riskPoolSharing.add.messages.noOrganizationsSelected'), {
        variant: 'warning',
      });
      return;
    }
    setActiveTab(1); // Switch to Selected Organizations tab
  };

  const handleAddOrganizations = async () => {
    if (!currentOrganizationId) return;

    if (selectedCount === 0) {
      enqueueSnackbar(t('riskPoolSharing.add.messages.noOrganizationsSelected'), {
        variant: 'warning',
      });
      return;
    }

    setIsSubmitting(true);

    let successCount = 0;
    let failedCount = 0;

    // Get yesterday for effectiveStart and 1 year from now for effectiveEnd
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const oneYearFromNow = dayjs().add(1, 'year').format('YYYY-MM-DD');
    const createdBy = currentProfile?.user?.email || currentProfile?.user?.username || 'system';

    // Loop through selected organizations and add them one by one
    for (const org of selectedOrganizations) {
      try {
        await createRiskPoolRelation(
          currentOrganizationId,
          {
            childOrgId: org.organization.id,
            effectiveStart: yesterday,
            effectiveEnd: oneYearFromNow,
            hierarchyType: 'RISK_POOL',
            reason: 'Added via Risk Pool Sharing UI',
            createdBy,
          },
          (errors) => {
            errorHelper.enqueueErrors(errors);
          }
        );
        successCount++;
      } catch (error) {
        // Error already handled in createRiskPoolRelation callback
        failedCount++;
      }
    }

    setIsSubmitting(false);

    // Invalidate the risk pool organizations query to refresh the list
    if (successCount > 0 && queryClient) {
      queryClient.invalidateQueries(['riskPoolOrganizations']);
    }

    // Show appropriate message
    if (failedCount === 0) {
      const message = t('riskPoolSharing.add.messages.addedSuccessfully').replace('{{count}}', successCount.toString());
      enqueueSnackbar(message, { variant: 'success' });
    } else {
      const message = t('riskPoolSharing.add.messages.addedWithErrors')
        .replace('{{success}}', successCount.toString())
        .replace('{{failed}}', failedCount.toString());
      enqueueSnackbar(message, { variant: 'warning' });
    }

    // Navigate back to list
    navigate('/admin/lexid/risk-pool-sharing');
  };

  const availableTabProps = {
    debouncedSearch,
    debouncedJurisdiction,
    setAvailableJurisdictions,
    selectedOrganizations,
    toggleOrganization,
    isSelected,
    existingOrgIds,
  };

  const selectedTabProps = {
    selectedOrganizations,
    toggleOrganization,
  };

  const getActionButton = () => {
    if (activeTab === 0) {
      // Available Organizations tab - Show Continue button
      return (
        <Button
          variant="contained"
          size="small"
          onClick={handleContinue}
          disabled={selectedCount === 0}
          sx={{ height: 40, minWidth: 140 }}
        >
          {t('riskPoolSharing.add.actions.continue')}
        </Button>
      );
    } else {
      // Selected Organizations tab - Show Add Organizations button
      return (
        <Button
          variant="contained"
          size="small"
          onClick={handleAddOrganizations}
          disabled={selectedCount === 0 || isSubmitting}
          sx={{ height: 40, minWidth: 170 }}
        >
          {isSubmitting
            ? t('riskPoolSharing.add.messages.addingOrganizations')
            : t('riskPoolSharing.add.actions.addOrganizations')}
        </Button>
      );
    }
  };

  return (
    <TakeoverLayout
      headerProps={{
        title: t('riskPoolSharing.title'),
        subtitle: t('riskPoolSharing.subtitle'),
      }}
      backUrl='/admin/lexid/risk-pool-sharing'
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', gap: 3, minHeight: 0 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={t('riskPoolSharing.add.tabs.allOrganizations')} />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('riskPoolSharing.add.tabs.selectedOrganizations')}
                  {selectedCount > 0 && <PhxBadgeCounter number={selectedCount} color="default" size="sm" />}
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Toolbar with Filters and Actions */}
        <PageToolbar
          sx={{ display: 'flex', alignItems: 'start' }}
          leftItems={
            activeTab === 0 && (
              <Stack
                direction={isMobile ? 'column' : 'row'}
                alignItems="center"
                sx={{
                  width: isMobile ? '100%' : 'auto',
                  flexWrap: 'wrap',
                  alignContent: 'flex-start',
                  gap: 2,
                  maxWidth: '100%',
                }}
              >
                <QuickSearch
                  id="add_org_search"
                  initialValue={searchInput}
                  onChange={(val) => setSearchInput(val)}
                  placeholder={t('riskPoolSharing.filters.searchByOrganization')}
                  minWidth={isMobile ? undefined : 180}
                  fullWidth={isMobile}
                  sx={{
                    flexGrow: isMobile ? 0 : 1,
                    maxWidth: isMobile ? '100%' : 220,
                    '& .MuiInputBase-root': {
                      height: 40,
                    },
                  }}
                />
                <FormControl
                  size="small"
                  sx={{
                    minWidth: isMobile ? '100%' : 180,
                    flexGrow: isMobile ? 0 : 1,
                    maxWidth: isMobile ? '100%' : 220,
                    padding: 0
                  }}
                >
                  <Select
                    id="add-jurisdiction-filter"
                    value={jurisdictionInput}
                    onChange={(e) => setJurisdictionInput(e.target.value)}
                    displayEmpty
                    sx={{ height: 40 }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return `${t('riskPoolSharing.filters.searchByJurisdiction')}: All`;
                      }
                      return `${t('riskPoolSharing.filters.searchByJurisdiction')}: ${selected}`;
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {availableJurisdictions.map((jurisdiction) => (
                      <MenuItem key={jurisdiction.value} value={jurisdiction.value}>
                        {jurisdiction.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )
          }
          rightItems={getActionButton()}
        />

        {/* Tab Content */}
        <Box sx={{ minHeight: 0, overflow: 'hidden' }}>
          {activeTab === 0 ? (
            <AvailableOrganizationsTab {...availableTabProps} />
          ) : (
            <SelectedOrganizationsTab {...selectedTabProps} />
          )}
        </Box>
      </Box>
    </TakeoverLayout>
  );
};

export default SearchOrganizations;

